import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Typography,
  Box,
  CircularProgress,
  LinearProgress,
  IconButton,
  Chip,
  Grid,
} from '@mui/material';
import { format, addDays } from 'date-fns';
import { Close, CheckCircle, AccessTime, People, CalendarToday } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import useResy from '../../hooks/useResy';
import { Activity, ResyVenue, ResyVenueTimeSlot } from '../../types/interfaces';
import { formatTimeSlotDate } from '../../lib/utils';
import { Button } from '../Button/Button';
import './ResyIntegrationModal.scss';

interface ResyIntegrationModalProps {
  open: boolean;
  activity: Activity;
  onClose: () => void;
  onBookingComplete: (reservation: any, venue: ResyVenue, slot: ResyVenueTimeSlot) => void;
}

const ResyIntegrationModal: React.FC<ResyIntegrationModalProps> = ({
  open,
  activity,
  onClose,
  onBookingComplete,
}) => {
  const { find, book } = useResy();
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedDateTime, setSelectedDateTime] = useState<Date | null>(new Date());
  const [partySize, setPartySize] = useState(2);
  const [venue, setVenue] = useState<ResyVenue | null>(null);
  const [availableSlots, setAvailableSlots] = useState<ResyVenueTimeSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<ResyVenueTimeSlot | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);

  const totalSteps = 4; // Check time, select time, show options, confirm
  const progressPercentage = ((currentStep + 1) / totalSteps) * 100;

  const checkExistingReservation = useCallback(async () => {
    // Check if there's already a reservation for this activity
    if (activity.resy?.reservation) {
      setCurrentStep(0);
      // Show existing reservation
    } else {
      setCurrentStep(1);
      // Start booking flow
    }
  }, [activity.resy?.reservation]);

  useEffect(() => {
    if (open) {
      checkExistingReservation();
    } else {
      resetModal();
    }
  }, [open, checkExistingReservation]);

  const searchAvailability = async () => {
    if (!selectedDateTime || !activity.resyId) return;

    setIsLoading(true);
    try {
      // Create a modified activity with the selected date and party size
      const searchActivity = {
        ...activity,
        day: format(selectedDateTime, 'yyyy-MM-dd'),
        party_size: partySize,
      };

      const venueData = await find(searchActivity);
      setVenue(venueData);

      // Filter slots based on selected date/time preference
      const targetTime = selectedDateTime.getHours() * 60 + selectedDateTime.getMinutes();
      const filteredSlots = venueData.slots.filter((slot: ResyVenueTimeSlot) => {
        const slotDate = new Date(slot.date.start);
        const slotTime = slotDate.getHours() * 60 + slotDate.getMinutes();
        // Show slots within 2 hours of preferred time
        return Math.abs(slotTime - targetTime) <= 120;
      }).slice(0, 5); // Show max 5 options

      setAvailableSlots(filteredSlots);
      setCurrentStep(2);
    } catch (error) {
      console.error('Error finding availability:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSlotSelection = (slot: ResyVenueTimeSlot) => {
    setSelectedSlot(slot);
    setCurrentStep(3);
  };

  const confirmBooking = async () => {
    if (!venue || !selectedSlot) return;

    setIsLoading(true);
    try {
      const reservation = await book(venue, selectedSlot);
      setBookingComplete(true);
      onBookingComplete(reservation, venue, selectedSlot);

      // Show success animation for 2 seconds before closing
      setTimeout(() => {
        onClose();
        resetModal();
      }, 2000);
    } catch (error) {
      console.error('Error booking reservation:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const resetModal = () => {
    setCurrentStep(0);
    setSelectedDateTime(new Date());
    setPartySize(2);
    setVenue(null);
    setAvailableSlots([]);
    setSelectedSlot(null);
    setBookingComplete(false);
  };

  const renderStepContent = () => {
    if (bookingComplete) {
      return (
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="booking-complete"
        >
          <CheckCircle className="success-icon" color="success" />
          <Typography variant="h5">Reservation Confirmed!</Typography>
          <Typography variant="body2" color="textSecondary">
            {venue?.venue.id.resy && `Table for ${selectedSlot?.size.max} at ${formatTimeSlotDate(selectedSlot?.date.start || '').hour}:${formatTimeSlotDate(selectedSlot?.date.start || '').minute}`}
          </Typography>
        </motion.div>
      );
    }

    switch (currentStep) {
      case 0:
        if (activity.resy?.reservation) {
          return (
            <Box className="existing-reservation">
              <Typography variant="h6">You have an existing reservation</Typography>
              <Typography variant="body2">
                {`${formatTimeSlotDate(activity.resy.slot.date.start).hour}:${formatTimeSlotDate(activity.resy.slot.date.start).minute} - Party of ${activity.resy.slot.size.max}`}
              </Typography>
              <Button
                intent="secondary"
                onClick={() => setCurrentStep(1)}
                style={{ marginTop: '16px' }}
              >
                Change Reservation
              </Button>
            </Box>
          );
        }
        return null;

      case 1:
        return (
          <Box className="time-selection">
            <Typography variant="h6" gutterBottom>
              When would you like to dine?
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                Select Date
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {[0, 1, 2, 3, 4, 5, 6].map((daysFromNow) => {
                  const date = addDays(new Date(), daysFromNow);
                  const isSelected = selectedDateTime?.toDateString() === date.toDateString();
                  return (
                    <Chip
                      key={daysFromNow}
                      label={daysFromNow === 0 ? 'Today' : daysFromNow === 1 ? 'Tomorrow' : format(date, 'EEE, MMM d')}
                      onClick={() => {
                        const newDate = new Date(date);
                        if (selectedDateTime) {
                          newDate.setHours(selectedDateTime.getHours());
                          newDate.setMinutes(selectedDateTime.getMinutes());
                        }
                        setSelectedDateTime(newDate);
                      }}
                      color={isSelected ? 'primary' : 'default'}
                      variant={isSelected ? 'filled' : 'outlined'}
                    />
                  );
                })}
              </Box>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                Preferred Time
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {['5:00 PM', '5:30 PM', '6:00 PM', '6:30 PM', '7:00 PM', '7:30 PM', '8:00 PM', '8:30 PM', '9:00 PM'].map((time) => {
                  const [timeStr, period] = time.split(' ');
                  const [hours, minutes] = timeStr.split(':');
                  const hour24 = period === 'PM' && hours !== '12' ? parseInt(hours) + 12 : parseInt(hours);
                  const isSelected = selectedDateTime?.getHours() === hour24 && selectedDateTime?.getMinutes() === parseInt(minutes);
                  return (
                    <Chip
                      key={time}
                      label={time}
                      onClick={() => {
                        const newDate = selectedDateTime ? new Date(selectedDateTime) : new Date();
                        newDate.setHours(hour24);
                        newDate.setMinutes(parseInt(minutes));
                        setSelectedDateTime(newDate);
                      }}
                      color={isSelected ? 'primary' : 'default'}
                      variant={isSelected ? 'filled' : 'outlined'}
                      size="small"
                    />
                  );
                })}
              </Box>
            </Box>
            <Box className="party-size-selector" sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                Party Size
              </Typography>
              <Box display="flex" gap={1}>
                {[1, 2, 3, 4, 5, 6].map((size) => (
                  <Chip
                    key={size}
                    label={size}
                    onClick={() => setPartySize(size)}
                    color={partySize === size ? 'primary' : 'default'}
                    variant={partySize === size ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
            </Box>
            <Button
              intent="primary"
              onClick={searchAvailability}
              disabled={!selectedDateTime}
              fullWidth
              style={{ marginTop: '24px' }}
            >
              Search Availability
            </Button>
          </Box>
        );

      case 2:
        return (
          <Box className="slot-selection">
            <Typography variant="h6" gutterBottom>
              Available Reservations
            </Typography>
            {availableSlots.length === 0 ? (
              <Box textAlign="center" py={3}>
                <Typography variant="body2" color="textSecondary">
                  No available slots found for your preferred time
                </Typography>
                <Button
                  intent="secondary"
                  onClick={() => setCurrentStep(1)}
                  style={{ marginTop: '16px' }}
                >
                  Adjust Parameters
                </Button>
              </Box>
            ) : (
              <Grid container spacing={2} className="reservations-grid">
                {availableSlots.map((slot, index) => {
                  const slotDate = formatTimeSlotDate(slot.date.start);
                  return (
                    <Grid item xs={12} key={index}>
                      <Button
                        intent="ghost"
                        fullWidth
                        onClick={() => handleSlotSelection(slot)}
                        className="reservation-button"
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <AccessTime fontSize="small" />
                          <Typography>
                            {`${slotDate.hour}:${slotDate.minute.toString().padStart(2, '0')}`}
                          </Typography>
                          <People fontSize="small" />
                          <Typography>{slot.size.max}</Typography>
                        </Box>
                      </Button>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        );

      case 3:
        if (!selectedSlot) return null;
        const confirmDate = formatTimeSlotDate(selectedSlot.date.start);
        return (
          <Box className="confirmation">
            <Typography variant="h6" gutterBottom>
              Confirm Your Reservation
            </Typography>
            <Box className="reservation-details" sx={{ my: 3 }}>
              <Typography variant="body1" gutterBottom>
                <strong>{activity.name}</strong>
              </Typography>
              <Box display="flex" alignItems="center" gap={1} sx={{ mt: 1 }}>
                <CalendarToday fontSize="small" />
                <Typography variant="body2">
                  {new Date(selectedSlot.date.start).toLocaleDateString()}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1} sx={{ mt: 1 }}>
                <AccessTime fontSize="small" />
                <Typography variant="body2">
                  {`${confirmDate.hour}:${confirmDate.minute.toString().padStart(2, '0')}`}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1} sx={{ mt: 1 }}>
                <People fontSize="small" />
                <Typography variant="body2">
                  Party of {selectedSlot.size.max}
                </Typography>
              </Box>
            </Box>
            <Button
              intent="primary"
              onClick={confirmBooking}
              disabled={isLoading}
              fullWidth
            >
              {isLoading ? <CircularProgress size={24} /> : 'Confirm Booking'}
            </Button>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Modal open={open} onClose={onClose} className="resy-integration-modal">
      <Card className="resy-booking-card glassmorphic-card animate-hover">
        <Box className="progress-container">
          <LinearProgress
            variant="determinate"
            value={progressPercentage}
            className="glowing-progress"
          />
        </Box>
        <CardHeader
          title={activity.name}
          subheader="Resy Reservation"
          action={
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          }
        />
        <CardContent>
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {isLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : (
                renderStepContent()
              )}
            </motion.div>
          </AnimatePresence>
        </CardContent>
      </Card>
    </Modal>
  );
};

export default ResyIntegrationModal; 